import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ORDER_RECEIPT_DETAILS_URL = `${API_URL}/order-receipt-details`;
const ORDER_DEDUCTIONS_URL = `${API_URL}/order-deductions`;
const LOV_URL = `${API_URL}/commonLOVs`;

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const receiptService = {
  // Get all receipt lines for an order (all types: bank, refund, wallet, etc.)
  getAllReceiptLinesByOrderId: async (orderId) => {
    try {
      const response = await axios.get(
        `${ORDER_RECEIPT_DETAILS_URL}/byOrderId/${orderId}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch receipt lines' };
    }
  },

  // Get receipt lines by order ID and receipt type
  getReceiptLinesByOrderId: async (orderId, receiptTypeId = 1) => {
    try {
      const response = await axios.get(
        `${ORDER_RECEIPT_DETAILS_URL}/byTypeTransactionId/${orderId}/${receiptTypeId}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch receipt lines' };
    }
  },

  // Get receipt details by receipt ID
  getReceiptDetailsById: async (receiptId) => {
    try {
      const response = await axios.get(
        `${ORDER_RECEIPT_DETAILS_URL}/byReceiptId/${receiptId}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch receipt details' };
    }
  },

  // Get all receipt names (LOV)
  getReceiptNames: async () => {
    try {
      const response = await axios.get(`${LOV_URL}/lhreceipts`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch receipt names' };
    }
  },

  // Get order paid total
  getOrderPaidTotal: async (orderId) => {
    try {
      const response = await axios.get(
        `${ORDER_RECEIPT_DETAILS_URL}/order-paid-total/${orderId}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch order paid total' };
    }
  },

  // Create receipt line
  createReceiptLine: async (receiptData) => {
    try {
      const response = await axios.post(ORDER_RECEIPT_DETAILS_URL, receiptData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create receipt line' };
    }
  },

  // Delete receipt line
  deleteReceiptLine: async (receiptLineId, receiptId, receiptAmount) => {
    try {
      const response = await axios.delete(
        `${ORDER_RECEIPT_DETAILS_URL}/${receiptLineId}/${receiptId}/${receiptAmount}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete receipt line' };
    }
  },

  // Get deductions by order ID
  getDeductionsByOrderId: async (orderId) => {
    try {
      const response = await axios.get(
        `${ORDER_DEDUCTIONS_URL}/byOrderId/${orderId}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch deductions' };
    }
  },

  // Create deduction
  createDeduction: async (deductionData) => {
    try {
      const response = await axios.post(ORDER_DEDUCTIONS_URL, deductionData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create deduction' };
    }
  },

  // Generate auto deductions
  generateAutoDeductions: async (deductionParams) => {
    try {
      const response = await axios.post(
        `${ORDER_DEDUCTIONS_URL}/auto-deductions`,
        deductionParams,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to generate deductions' };
    }
  },

  // Delete deduction
  deleteDeduction: async (deductionId, expenseId) => {
    try {
      const response = await axios.delete(
        `${ORDER_DEDUCTIONS_URL}/${deductionId}/${expenseId}`,
        { headers: getAuthHeader() }
      );
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete deduction' };
    }
  }
};

export default receiptService;
